import { useMemo } from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import { WarningAmber } from '@mui/icons-material';
import { LABEL_W, DEF_ROW_H, MIN_TAT, toMin } from './constants';

const WeekRotationRow = ({ rotation, isSelected, theme, dayW, barColor, onClick, rowHeight = DEF_ROW_H }) => {
  const totalW = dayW * 7;

  const expandedLegs = useMemo(() => {
    const result = [];
    for (const leg of (rotation.legs || [])) {
      const days = (leg.daysOfOperation || '').split('').filter((d) => /[1-7]/.test(d));
      for (const d of days) {
        const dayIdx = parseInt(d, 10) - 1;
        const stdMin = toMin(leg.std);
        const blk    = leg.blockTime || 60;
        result.push({ ...leg, dayIdx, stdMin, blk });
      }
    }
    return result.sort((a, b) => (a.dayIdx * 1440 + a.stdMin) - (b.dayIdx * 1440 + b.stdMin));
  }, [rotation.legs]);

  const groundGaps = useMemo(() => {
    const byDay = {};
    for (const el of expandedLegs) {
      (byDay[el.dayIdx] = byDay[el.dayIdx] || []).push(el);
    }
    const gaps = [];
    for (const legs of Object.values(byDay)) {
      const sorted = [...legs].sort((a, b) => a.stdMin - b.stdMin);
      for (let i = 1; i < sorted.length; i++) {
        const prev = sorted[i - 1];
        const curr = sorted[i];
        const gs = prev.stdMin + prev.blk;
        const ge = curr.stdMin;
        if (ge > gs) {
          const tatOk = !curr.tat || curr.tat >= MIN_TAT;
          gaps.push({ dayIdx: curr.dayIdx, gs, ge, tatOk, tat: curr.tat });
        }
      }
    }
    return gaps;
  }, [expandedLegs]);

  const overnightMarkers = useMemo(() => {
    const byDay = {};
    for (const el of expandedLegs) {
      (byDay[el.dayIdx] = byDay[el.dayIdx] || []).push(el);
    }
    return Object.entries(byDay).map(([dayStr, legs]) => {
      const sorted = [...legs].sort((a, b) => a.stdMin - b.stdMin);
      const last = sorted[sorted.length - 1];
      return { dayIdx: parseInt(dayStr, 10), endMin: last.stdMin + last.blk, station: last.arr };
    });
  }, [expandedLegs]);

  return (
    <Box 
      onClick={onClick} 
      sx={{
        display: 'flex', 
        minHeight: rowHeight, 
        minWidth: LABEL_W + totalW,
        borderBottom: `1px solid ${theme.palette.divider}`,
        bgcolor: isSelected ? 'action.selected' : 'transparent',
        '&:hover': { bgcolor: 'action.hover', cursor: 'pointer' },
        transition: 'background-color 0.12s',
      }}
    >

      {/* Sticky label */}
      <Box 
        sx={{
          zIndex: 5,
          position: 'sticky', 
          left: 0, 
          flexShrink: 0,
          width: LABEL_W, 
          px: 1.2, 
          py: 0.6, 
          display: 'flex', 
          flexDirection: 'column', 
          justifyContent: 'center',
          borderRight: `1px solid ${theme.palette.divider}`,
          bgcolor: isSelected ? 'primary.darkest' : 'background.alt',
          boxShadow: isSelected ? `inset 3px 0 0 ${barColor}` : `2px 0 4px ${theme.palette.action.disabledBackground}`,
        }}
      >
        <Box display="flex" alignItems="center" gap={0.5} mb={0.2}>
          <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: barColor, flexShrink: 0 }} />
          {!rotation.isValid && <WarningAmber sx={{ fontSize: '0.7rem', color: '#ff9800', flexShrink: 0 }} />}
          <Typography 
            sx={{
              fontSize: '0.73rem', 
              fontFamily: 'monospace', 
              fontWeight: 'bold',
              color: isSelected ? 'primary.main' : 'text.primary',
              lineHeight: 1, 
              overflow: 'hidden', 
              textOverflow: 'ellipsis', 
              whiteSpace: 'nowrap',
            }}
          >
            {rotation.rotationCode}
          </Typography>
        </Box>
        <Typography 
          sx={{ 
            fontSize: '0.65rem', 
            fontFamily: 'monospace', 
            color: 'text.secondary', 
            lineHeight: 1 
          }}
        >
          {rotation.acType}{rotation.acReg ? ` · ${rotation.acReg}` : ''}
        </Typography>
        <Typography 
          sx={{ 
            fontSize: '0.52rem', 
            fontFamily: 'monospace', 
            color: 'text.disabled', 
            mt: 0.25, 
            lineHeight: 1 
          }}
        >
          {rotation.legs?.length || 0} legs
          {rotation.weeklyBlockHours > 0 ? ` · ${rotation.weeklyBlockHours}h BLK/wk` : ''}
        </Typography>
      </Box>

      {/* 7-day timeline */}
      <Box sx={{ width: totalW, flexShrink: 0, position: 'relative', minHeight: rowHeight }}>

        {/* Day column backgrounds */}
        {[0, 1, 2, 3, 4, 5, 6].map((d) => (
          <Box 
            key={d} 
            sx={{
              position: 'absolute', 
              left: d * dayW, 
              width: dayW, 
              top: 0, 
              bottom: 0,
              borderRight: `2px solid ${d >= 5 ? 'rgba(200,130,30,0.22)' : 'rgba(255,255,255,0.05)'}`,
              bgcolor: d >= 5 ? 'rgba(255,180,30,0.025)' : d % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.012)',
              pointerEvents: 'none',
            }} 
          />
        ))}

        {/* Night shading (00-06 and 22-24) per day */}
        {[0, 1, 2, 3, 4, 5, 6].flatMap((d) => [
          <Box key={`n1-${d}`} sx={{ position: 'absolute', left: d * dayW, width: (6 / 24) * dayW, top: 0, bottom: 0, bgcolor: 'rgba(0,0,0,0.22)', pointerEvents: 'none' }} />,
          <Box key={`n2-${d}`} sx={{ position: 'absolute', left: d * dayW + (22 / 24) * dayW, width: (2 / 24) * dayW, top: 0, bottom: 0, bgcolor: 'rgba(0,0,0,0.22)', pointerEvents: 'none' }} />,
        ])}

        {/* Hour grid lines */}
        {[0, 1, 2, 3, 4, 5, 6].flatMap((d) =>
          [0, 3, 6, 9, 12, 15, 18, 21].map((h) => (
            <Box key={`${d}-${h}`} sx={{
              position: 'absolute', left: d * dayW + (h / 24) * dayW, top: 0, bottom: 0, width: 1,
              bgcolor: theme.palette.divider, opacity: h % 6 === 0 ? 0.45 : 0.14, pointerEvents: 'none',
            }} />
          ))
        )}

        {/* Ground time gap indicators */}
        {groundGaps.map((gap, i) => {
          const left   = gap.dayIdx * dayW + (gap.gs / 1440) * dayW;
          const width  = Math.max(2, ((gap.ge - gap.gs) / 1440) * dayW);
          const tatTxt = gap.tat ? `${gap.tat}min GND` : 'GND';
          return (
            <Tooltip key={`gap-${i}`} title={`Ground time: ${tatTxt}${!gap.tatOk ? ' ⚠ TAT violation' : ''}`}>
              <Box sx={{
                position: 'absolute', left, width,
                top: '62%', height: '22%',
                bgcolor: gap.tatOk ? 'rgba(76,175,80,0.22)' : 'rgba(244,67,54,0.38)',
                borderBottom: `2px dashed ${gap.tatOk ? 'rgba(76,175,80,0.75)' : 'rgba(244,67,54,0.85)'}`,
                zIndex: 1,
              }}>
                {width > 28 && (
                  <Typography sx={{ fontSize: '0.46rem', fontFamily: 'monospace', color: gap.tatOk ? 'rgba(76,175,80,0.9)' : 'rgba(244,67,54,0.9)', lineHeight: 1, px: 0.2, whiteSpace: 'nowrap' }}>
                    {tatTxt}
                  </Typography>
                )}
              </Box>
            </Tooltip>
          );
        })}

        {/* Flight bars */}
        {expandedLegs.map((el, i) => {
          const left    = el.dayIdx * dayW + (el.stdMin / 1440) * dayW;
          const width   = Math.max(4, (el.blk / 1440) * dayW);
          const tatOk   = !el.tat || el.tat >= MIN_TAT;
          const showFlt = width > 24;
          const showRte = width > 50;
          const showTim = width > 90;
          return (
            <Tooltip key={i} arrow title={
              `${el.carrier || ''}${el.flightNo}  ${el.dep}→${el.arr}  ${el.std}–${el.sta}${el.nextDay ? `+${el.nextDay}` : ''}` +
              `  |  BLK ${Math.floor(el.blk / 60)}h${String(el.blk % 60).padStart(2, '0')}m` +
              (el.tat ? `  |  TAT ${el.tat}min${!tatOk ? ' ⚠ SHORT' : ''}` : '')
            }>
              <Box sx={{
                position: 'absolute', left, width,
                top: '8%', height: '76%',
                bgcolor: barColor,
                border: !tatOk ? '2px solid #f44336' : '1px solid rgba(255,255,255,0.18)',
                borderRadius: '3px', overflow: 'hidden',
                display: 'flex', alignItems: 'center', px: 0.5,
                zIndex: 2,
                '&:hover': { filter: 'brightness(1.18)', zIndex: 4, transform: 'scaleY(1.05)' },
                transition: 'filter 0.1s, transform 0.1s',
              }}>
                {showFlt && (
                  <Box sx={{ overflow: 'hidden', lineHeight: 1 }}>
                    <Typography sx={{ fontSize: '0.59rem', color: '#fff', fontFamily: 'monospace', fontWeight: 'bold', lineHeight: 1.25, whiteSpace: 'nowrap', textShadow: '0 1px 3px rgba(0,0,0,0.55)' }}>
                      {el.carrier || ''}{el.flightNo}
                    </Typography>
                    {showRte && (
                      <Typography sx={{ fontSize: '0.54rem', color: 'rgba(255,255,255,0.85)', fontFamily: 'monospace', lineHeight: 1.1, whiteSpace: 'nowrap' }}>
                        {el.dep}→{el.arr}
                      </Typography>
                    )}
                    {showTim && (
                      <Typography sx={{ fontSize: '0.5rem', color: 'rgba(255,255,255,0.65)', fontFamily: 'monospace', lineHeight: 1, whiteSpace: 'nowrap' }}>
                        {el.std}–{el.sta}{el.nextDay ? `+${el.nextDay}` : ''}
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>
            </Tooltip>
          );
        })}

        {/* Overnight station markers */}
        {overnightMarkers.map((m, i) => {
          const left = m.dayIdx * dayW + (Math.min(m.endMin, 1440) / 1440) * dayW;
          return (
            <Tooltip key={`ovn-${i}`} title={`Overnight: ${m.station}`}>
              <Box sx={{
                position: 'absolute', left: left - 3, top: '15%',
                width: 6, height: '70%',
                bgcolor: 'rgba(255,215,0,0.3)',
                borderLeft: '2px solid rgba(255,215,0,0.88)',
                zIndex: 3, pointerEvents: 'none',
              }}>
                {dayW > 80 && (
                  <Typography sx={{ fontSize: '0.46rem', color: 'rgba(255,215,0,0.9)', fontFamily: 'monospace', fontWeight: 'bold', ml: '3px', mt: '2px', lineHeight: 1, whiteSpace: 'nowrap' }}>
                    {m.station}
                  </Typography>
                )}
              </Box>
            </Tooltip>
          );
        })}
      </Box>
    </Box>
  );
};

export default WeekRotationRow;
